/**
 * Guitar-mode milestone fanfare polish. Wraps the shared
 * {@link scheduleMilestoneFanfare} arpeggio with two Guitar-specific
 * layers so a star / cup celebration reads as "the venue is going
 * off", not just "a chime played":
 *
 *   - **Riser sweep**: bandpassed noise climbing from low-mids to
 *     bright treble across ~180 ms. Lands its peak right under the
 *     first arp note so the chord feels "launched" by the build.
 *   - **Crowd cheer**: broadband noise with a slow amplitude
 *     wobble (≈4 Hz) and a long, soft swell. Filtered to avoid
 *     subs (no rumble) and harsh hiss (no static).
 *
 * Tier-aware so larger milestones (cup_2 / star_3) get a fuller riser
 * + crowd burst than star_1 — the audio scales with the visual.
 * Levels are pre-trimmed so the stack stays under the master
 * headroom even when the stage-bus compressor isn't engaged.
 */

import { scheduleMilestoneFanfare } from './sfx';

/** Trim applied to riser + cheer so they sit under the fanfare chord. */
const MILESTONE_LAYER_TRIM = 0.55;

/**
 * Compose the full Guitar milestone fanfare: riser sweep landing into
 * the chord, then the chord + bell from {@link scheduleMilestoneFanfare},
 * with a crowd cheer swelling underneath. All three layers fire from
 * the same `t0` so the riser's peak coincides with the first chord
 * note (the riser's gain envelope front-loads its peak).
 */
export function scheduleGuitarMilestoneFanfare(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  tier: 1 | 2 | 3,
): void {
  scheduleMilestoneRiser(ctx, destination, t0, tier);
  scheduleMilestoneFanfare(ctx, destination, t0, tier);
  scheduleMilestoneCheer(ctx, destination, t0, tier);
}

/**
 * Filtered-noise sweep climbing into the chord. Builds tension for
 * ~180 ms then drops out as the fanfare's bell takes over. Tier 3
 * sweeps wider + a touch louder than tier 1 so star_3 announces
 * itself harder than star_1.
 */
export function scheduleMilestoneRiser(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  tier: 1 | 2 | 3,
): void {
  const duration = tier === 3 ? 0.22 : tier === 2 ? 0.2 : 0.18;
  const samples = Math.max(1, Math.ceil(ctx.sampleRate * duration));
  const buffer = ctx.createBuffer(1, samples, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < samples; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  const startHz = tier === 3 ? 240 : 300;
  const endHz = tier === 3 ? 2400 : tier === 2 ? 2000 : 1700;
  filter.frequency.setValueAtTime(startHz, t0);
  filter.frequency.exponentialRampToValueAtTime(endHz, t0 + duration);
  filter.Q.value = 4.5;

  const peak = (tier === 3 ? 0.38 : tier === 2 ? 0.32 : 0.27) * MILESTONE_LAYER_TRIM;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + duration * 0.85);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration + 0.04);

  noise.connect(filter).connect(gain).connect(destination);
  noise.start(t0);
}

/**
 * Crowd-cheer burst: broadband filtered noise with a slow ~4 Hz
 * amplitude wobble for that "many voices" texture. Long soft swell
 * so it sits under the chord as ambience rather than competing
 * with it. Tier-aware on both duration and peak.
 */
export function scheduleMilestoneCheer(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  tier: 1 | 2 | 3,
): void {
  const duration = tier === 3 ? 1.2 : tier === 2 ? 1.0 : 0.85;
  const samples = Math.max(1, Math.ceil(ctx.sampleRate * duration));
  const buffer = ctx.createBuffer(1, samples, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  const wobbleHz = 4;
  const wobbleDepth = 0.35;
  for (let i = 0; i < samples; i += 1) {
    const t = i / ctx.sampleRate;
    const wobble = 1 - wobbleDepth + wobbleDepth * Math.abs(Math.sin(2 * Math.PI * wobbleHz * t));
    data[i] = (Math.random() * 2 - 1) * wobble;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;

  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 320;
  highpass.Q.value = 0.7;

  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 3800;
  lowpass.Q.value = 0.7;

  const peak = (tier === 3 ? 0.16 : tier === 2 ? 0.13 : 0.1) * MILESTONE_LAYER_TRIM;
  const gain = ctx.createGain();
  const attackEnd = t0 + duration * 0.22;
  const sustainStart = attackEnd;
  const releaseStart = t0 + duration * 0.55;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, attackEnd);
  gain.gain.setValueAtTime(peak, sustainStart);
  gain.gain.setValueAtTime(peak, releaseStart);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);

  noise.connect(highpass).connect(lowpass).connect(gain).connect(destination);
  noise.start(t0);
}
